import { forwardRef, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "./ui/input";
import { api, formatApiError } from "../lib/api";
import { useSalon } from "../lib/salon";
import { Check, WarningCircle, Globe } from "@phosphor-icons/react";

/**
 * PhoneInput — input filtering + inline canonical preview.
 *
 * IMPORTANT: This is UX only. The backend (POST /api/customers/normalize-phone
 * and the customer routes themselves) is the canonical source of truth.
 *
 * Allowed during typing: digits, '+', spaces, hyphens, parentheses.
 * Anything else is silently stripped (handles paste of "Tel: +353 ...").
 */
const ALLOWED = /[\d+\s().-]/;

function filterChars(s) {
  return Array.from(s || "")
    .filter((c) => ALLOWED.test(c))
    .join("")
    .replace(/(?!^)\+/g, "");
}

// Country-code → example placeholder fragment so receptionists can see the
// active default at a glance.
function placeholderFor(cc) {
  const map = {
    "+1": "+1 555 123 4567",
    "+44": "+44 7700 900123",
    "+353": "+353 85 123 4567",
    "+61": "+61 4XX XXX XXX",
    "+91": "+91 98765 43210",
  };
  if (cc && map[cc]) return map[cc];
  if (cc) return `${cc} 85 123 4567`;
  return "+1 555 123 4567";
}

const PhoneInput = forwardRef(function PhoneInput(
  {
    value,
    onChange,
    onCanonicalChange,
    placeholder,
    disabled = false,
    "data-testid": testId,
    className = "",
    showHint = true,
    ...rest
  },
  ref
) {
  const salon = useSalon();
  const cc = salon?.default_country_code || "+1";
  const [preview, setPreview] = useState({ status: "idle", canonical: "", error: "" });
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!value || !value.trim()) {
      setPreview({ status: "idle", canonical: "", error: "" });
      onCanonicalChange?.(null);
      return;
    }
    setPreview((p) => ({ ...p, status: "checking" }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/customers/normalize-phone", { phone: value });
        if (data.ok) {
          setPreview({ status: "ok", canonical: data.canonical, error: "" });
          onCanonicalChange?.(data.canonical);
        } else {
          setPreview({ status: "error", canonical: "", error: data.error || "Invalid phone" });
          onCanonicalChange?.(null);
        }
      } catch (e) {
        setPreview({
          status: "error",
          canonical: "",
          error: formatApiError(e.response?.data?.detail) || e.message,
        });
        onCanonicalChange?.(null);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e) => {
    const filtered = filterChars(e.target.value);
    onChange?.({ target: { value: filtered } });
  };

  const showAppliedDefault = preview.status === "ok" && !value?.trim().startsWith("+") && !value?.trim().startsWith("00");

  return (
    <div className="space-y-1">
      <Input
        ref={ref}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={value || ""}
        onChange={handleChange}
        placeholder={placeholder || placeholderFor(cc)}
        disabled={disabled}
        data-testid={testId}
        className={className}
        {...rest}
      />

      {/* Live canonical preview */}
      {preview.status === "ok" && (
        <div
          className="flex items-center gap-1.5 text-[11px] text-[#1A3626]"
          data-testid={testId ? `${testId}-canonical` : undefined}
        >
          <Check size={12} weight="bold" />
          <span className="font-mono">{preview.canonical}</span>
          {showAppliedDefault && (
            <span className="text-muted-foreground">· using default {cc}</span>
          )}
        </div>
      )}
      {preview.status === "error" && (
        <div
          className="flex items-center gap-1.5 text-[11px] text-destructive"
          data-testid={testId ? `${testId}-error` : undefined}
        >
          <WarningCircle size={12} weight="fill" />
          <span>{preview.error}</span>
        </div>
      )}

      {/* Always-visible default-country hint when input is empty */}
      {showHint && preview.status === "idle" && (
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid={testId ? `${testId}-default-hint` : undefined}>
          <Globe size={12} />
          <span>
            Local numbers default to <span className="font-mono text-foreground">{cc}</span>
          </span>
          <Link
            to="/settings"
            className="underline underline-offset-2 hover:text-foreground"
            data-testid={testId ? `${testId}-settings-link` : undefined}
          >
            change
          </Link>
        </div>
      )}
    </div>
  );
});

export default PhoneInput;
